/**
 * duelsService.js - Serviço para Sistema de Duelos 1v1
 * 
 * Usa a tabela 'duels_challenges' para convites de duelo
 * e 'duels' para partidas em andamento/concluídas.
 */

import { supabase } from '../../../lib/supabase';

// ==================== ENVIAR DESAFIO ====================

/**
 * Verifica se o usuário pode enviar um desafio (limite diário de 4)
 * @param {string} userId - ID do usuário
 * @returns {Promise<{canSend: boolean, remaining: number, resetsAt: Date}>}
 */
export const checkChallengeLimit = async (userId) => {
    try {
        const { data, error } = await supabase
            .rpc('check_challenge_limit', { p_user_id: userId });

        if (error) throw error;

        return {
            canSend: data?.[0]?.can_send ?? true,
            remaining: data?.[0]?.remaining ?? 4,
            resetsAt: data?.[0]?.resets_at ? new Date(data[0].resets_at) : new Date(),
        };
    } catch (error) {
        console.error('[Duels] Erro ao verificar limite:', error.message);
        // Em caso de erro, permite enviar
        return { canSend: true, remaining: 4, resetsAt: new Date() };
    }
};

/**
 * Envia um desafio para outro usuário
 * @param {string} challengerId - ID de quem desafia
 * @param {string} challengedId - ID de quem é desafiado
 * @returns {Promise<{success: boolean, challengeId?: string, error?: string}>}
 */
export const sendChallenge = async (challengerId, challengedId) => {
    try {
        const { data, error } = await supabase.rpc('send_duel_challenge', {
            p_challenger_id: challengerId,
            p_challenged_id: challengedId,
        });

        if (error) throw error;

        if (!data?.success) {
            const errorMessages = {
                ALREADY_PENDING: 'Você já tem um desafio pendente com este usuário',
                LIMIT_REACHED: 'Você atingiu o limite diário de desafios',
                SAME_USER: 'Você não pode desafiar a si mesmo',
                UNAUTHORIZED: 'Sessão inválida. Faça login novamente.',
                NOT_ENOUGH_QUESTIONS: 'Ainda não há questões suficientes para um duelo. Tente mais tarde.',
            };
            return {
                success: false,
                error: errorMessages[data?.error] || 'Erro ao enviar desafio. Tente novamente.',
            };
        }

        return {
            success: true,
            challengeId: data.challenge_id,
        };
    } catch (error) {
        console.error('[Duels] Erro ao enviar desafio:', error.message);
        return { success: false, error: 'Erro ao enviar desafio. Tente novamente.' };
    }
};

// ==================== RECEBER/ACEITAR DESAFIO ====================

/**
 * Obtém desafios pendentes recebidos pelo usuário
 * @param {string} userId - ID do usuário
 * @returns {Promise<Array>} Lista de desafios pendentes
 */
export const getPendingChallenges = async (userId) => {
    try {
        const { data, error } = await supabase
            .from('duels_challenges')
            .select('id, challenger_id, status, expires_at, created_at, questions')
            .eq('challenged_id', userId)
            .eq('status', 'pending')
            .gt('expires_at', new Date().toISOString())
            .order('created_at', { ascending: false });

        if (error) throw error;

        // Fetch challenger info separately
        if (data && data.length > 0) {
            const challengerIds = [...new Set(data.map(d => d.challenger_id))];
            const { data: profiles } = await supabase
                .from('profiles')
                .select('id, nome, username, foto_url, nivel')
                .in('id', challengerIds);

            const profileMap = {};
            profiles?.forEach(p => {
                profileMap[p.id] = {
                    ...p,
                    avatar_url: p.foto_url,
                    level: p.nivel || 1,
                };
            });

            return data.map(d => ({
                ...d,
                challenger: profileMap[d.challenger_id] || { username: 'Usuário' }
            }));
        }

        return data || [];
    } catch (error) {
        console.error('[Duels] Erro ao buscar desafios:', error.message);
        return [];
    }
};

/**
 * Obtém desafios enviados pelo usuário
 * @param {string} userId - ID do usuário
 * @returns {Promise<Array>} Lista de desafios enviados
 */
export const getSentChallenges = async (userId) => {
    try {
        const { data, error } = await supabase
            .from('duels_challenges')
            .select('id, challenged_id, status, expires_at, created_at, questions')
            .eq('challenger_id', userId)
            .in('status', ['pending', 'accepted'])
            .order('created_at', { ascending: false });

        if (error) throw error;

        // Fetch challenged user info separately
        if (data && data.length > 0) {
            const challengedIds = [...new Set(data.map(d => d.challenged_id))];
            const { data: profiles } = await supabase
                .from('profiles')
                .select('id, nome, username, foto_url, nivel')
                .in('id', challengedIds);

            const profileMap = {};
            profiles?.forEach(p => {
                profileMap[p.id] = {
                    ...p,
                    avatar_url: p.foto_url,
                    level: p.nivel || 1,
                };
            });

            return data.map(d => ({
                ...d,
                challenged: profileMap[d.challenged_id] || { username: 'Usuário' }
            }));
        }

        return data || [];
    } catch (error) {
        console.error('[Duels] Erro ao buscar desafios enviados:', error.message);
        return [];
    }
};

/**
 * Aceita um desafio
 * @param {string} challengeId - ID do desafio
 * @param {string} userId - ID do usuário que aceita
 * @returns {Promise<{success: boolean, duelId?: string, questions?: Array, error?: string}>}
 */
export const acceptChallenge = async (challengeId, userId) => {
    try {
        const { data, error } = await supabase
            .rpc('accept_challenge', {
                p_challenge_id: challengeId,
                p_user_id: userId,
            });

        if (error) throw error;

        if (!data?.success) {
            const errorMessages = {
                'NOT_FOUND': 'Desafio não encontrado',
                'INVALID_STATUS': 'Este desafio já foi respondido',
                'EXPIRED': 'Este desafio expirou',
            };
            return {
                success: false,
                error: errorMessages[data?.error] || 'Erro ao aceitar desafio',
            };
        }

        return {
            success: true,
            duelId: data.duel_id,
            questions: data.questions,
        };
    } catch (error) {
        console.error('[Duels] Erro ao aceitar desafio:', error.message);
        return { success: false, error: error.message };
    }
};

/**
 * Recusa um desafio
 * @param {string} challengeId - ID do desafio
 * @param {string} userId - ID do usuário que recusa
 */
export const declineChallenge = async (challengeId, userId) => {
    try {
        const { data, error } = await supabase
            .rpc('decline_challenge', {
                p_challenge_id: challengeId,
                p_user_id: userId,
            });

        if (error) throw error;

        if (!data?.success) {
            const errorMessages = {
                UNAUTHORIZED: 'Sessão inválida. Faça login novamente.',
                NOT_FOUND: 'Desafio não encontrado.',
                INVALID_STATUS: 'Este desafio já foi respondido.',
            };

            return {
                success: false,
                error: errorMessages[data?.error] || 'Erro ao recusar desafio.',
            };
        }

        return { success: true };
    } catch (error) {
        console.error('[Duels] Erro ao recusar desafio:', error.message);
        return { success: false, error: error.message };
    }
};

// ==================== DUELOS ====================

/**
 * Obtém duelo ativo do usuário
 * @param {string} userId - ID do usuário
 * @returns {Promise<Object|null>} Duelo ativo ou null
 */
export const getActiveDuel = async (userId) => {
    try {
        const { data, error } = await supabase
            .from('duels')
            .select('id, challenge_id, status, player1_id, player2_id, player1_score, player2_score, player1_ready, player2_ready, live_started_at, questions, total_questions, created_at')
            .or(`player1_id.eq.${userId},player2_id.eq.${userId}`)
            .in('status', ['in_progress', 'waiting_player2'])
            .limit(1)
            .maybeSingle();

        if (error) throw error;
        if (!data) return null;

        const isPlayer1 = data.player1_id === userId;
        const opponentId = isPlayer1 ? data.player2_id : data.player1_id;

        const { data: opponentProfile } = await supabase
            .from('profiles')
            .select('id, nome, username, foto_url, nivel')
            .eq('id', opponentId)
            .maybeSingle();

        const opponentUsername = (
            opponentProfile?.username ||
            opponentProfile?.nome ||
            opponentProfile?.email?.split('@')[0] ||
            'Oponente'
        );

        const questions = Array.isArray(data.questions) ? data.questions : [];
        const totalQuestions = data.total_questions || questions.length || 10;

        return {
            ...data,
            opponent: {
                id: opponentId,
                username: opponentUsername,
                nome: opponentProfile?.nome,
                avatarUrl: opponentProfile?.foto_url,
                level: opponentProfile?.nivel || 1,
            },
            userScore: isPlayer1 ? data.player1_score : data.player2_score,
            opponentScore: isPlayer1 ? data.player2_score : data.player1_score,
            totalQuestions,
            remainingTimeSeconds: 0,
        };
    } catch (error) {
        console.error('[Duels] Erro ao buscar duelo ativo:', error.message);
        return null;
    }
};

/**
 * Obtém histórico de duelos do usuário
 * @param {string} userId - ID do usuário
 * @param {number} limit - Limite de resultados
 * @returns {Promise<Array>} Lista de duelos
 */
export const getDuelHistory = async (userId, limit = 10) => {
    try {
        const { data, error } = await supabase
            .from('duels')
            .select('id, status, player1_id, player2_id, player1_score, player2_score, winner_id, result, xp_player1, xp_player2, completed_at')
            .or(`player1_id.eq.${userId},player2_id.eq.${userId}`)
            .eq('status', 'completed')
            .order('completed_at', { ascending: false })
            .limit(limit);

        if (error) throw error;

        // Adiciona campo isVictory e xpChange
        return (data || []).map(duel => ({
            ...duel,
            isVictory: duel.winner_id === userId,
            isDraw: duel.result === 'draw',
            xpChange: duel.player1_id === userId ? duel.xp_player1 : duel.xp_player2,
            userScore: duel.player1_id === userId ? duel.player1_score : duel.player2_score,
            opponentScore: duel.player1_id === userId ? duel.player2_score : duel.player1_score,
        }));
    } catch (error) {
        console.error('[Duels] Erro ao buscar histórico:', error.message);
        return [];
    }
};

/**
 * Registra uma resposta no duelo
 * @param {string} duelId - ID do duelo
 * @param {string} userId - ID do usuário
 * @param {string} questionId - ID da questão
 * @param {number} questionIndex - Índice da questão
 * @param {number} selectedOption - Opção selecionada
 * @param {boolean} isCorrect - Se a resposta está correta
 * @param {number} timeTakenMs - Tempo em ms
 */
export const submitDuelAnswer = async (duelId, userId, questionId, questionIndex, selectedOption, isCorrect, timeTakenMs) => {
    try {
        const { data, error } = await supabase
            .rpc('submit_duel_answer', {
                p_duel_id: duelId,
                p_question_id: questionId,
                p_question_index: questionIndex,
                p_selected_option: selectedOption,
                p_time_taken_ms: timeTakenMs,
            });

        if (error) throw error;

        if (!data?.success) {
            const errorMessages = {
                UNAUTHORIZED: 'Sessão inválida. Faça login novamente.',
                NOT_FOUND: 'Duelo não encontrado.',
                CANCELLED: 'Este duelo foi cancelado.',
                COMPLETED: 'Este duelo já foi finalizado.',
                NOT_STARTED: 'O duelo ainda não começou para os dois jogadores.',
                INVALID_QUESTION: 'Questão inválida para este duelo.',
                QUESTION_NOT_FOUND: 'Questão não encontrada.',
            };

            return {
                success: false,
                error: errorMessages[data?.error] || 'Erro ao registrar resposta. Tente novamente.',
            };
        }

        return {
            success: true,
            isCorrect: data.is_correct ?? isCorrect,
            duplicate: data.duplicate ?? false,
            xpGained: data.xp_gained ?? 0,
        };
    } catch (error) {
        console.error('[Duels] Erro ao registrar resposta:', error.message);
        return { success: false, error: error.message };
    }
};

/**
 * Finaliza o duelo e calcula resultados
 * @param {string} duelId - ID do duelo
 */
export const completeDuel = async (duelId) => {
    try {
        const { data, error } = await supabase
            .rpc('complete_duel', { p_duel_id: duelId });

        if (error) throw error;

        if (!data?.success) {
            const errorMessages = {
                UNAUTHORIZED: 'Sessão inválida. Faça login novamente.',
                NOT_FOUND: 'Duelo não encontrado.',
                CANCELLED: 'Este duelo foi cancelado.',
                NOT_STARTED: 'O duelo ainda não começou para os dois jogadores.',
            };

            return {
                success: false,
                error: errorMessages[data?.error] || 'Erro ao finalizar duelo.',
            };
        }

        return {
            success: true,
            winnerId: data?.winner_id,
            result: data?.result,
            xpPlayer1: data?.xp_player1,
            xpPlayer2: data?.xp_player2,
        };
    } catch (error) {
        console.error('[Duels] Erro ao finalizar duelo:', error.message);
        return { success: false, error: error.message };
    }
};

/**
 * Marca o jogador atual como pronto para entrar no duelo ao vivo.
 */
export const joinLiveDuel = async (duelId) => {
    try {
        const { data, error } = await supabase
            .rpc('join_live_duel', { p_duel_id: duelId });

        if (error) throw error;
        return { success: data?.success ?? false, data };
    } catch (error) {
        console.error('[Duels] Erro ao entrar no duelo ao vivo:', error.message);
        return { success: false, error: error.message };
    }
};

/**
 * Cancela/desiste de um duelo aceito antes do inicio ao vivo.
 */
export const cancelLiveDuel = async (duelId) => {
    try {
        const { data, error } = await supabase
            .rpc('cancel_live_duel', { p_duel_id: duelId });

        if (error) throw error;
        return { success: data?.success ?? false, data };
    } catch (error) {
        console.error('[Duels] Erro ao desistir do duelo:', error.message);
        return { success: false, error: error.message };
    }
};

// ==================== ESTATÍSTICAS ====================

/**
 * Obtém estatísticas do usuário em duelos
 * @param {string} userId - ID do usuário
 */
export const getUserDuelStats = async (userId) => {
    try {
        const { data, error } = await supabase
            .from('user_stats')
            .select('*')
            .eq('user_id', userId)
            .single();

        if (error && error.code !== 'PGRST116') throw error;

        return data || {
            total_duels: 0,
            total_wins: 0,
            total_losses: 0,
            total_draws: 0,
            win_rate: 0,
            current_streak: 0,
            longest_streak: 0,
            weekly_wins: 0,
            weekly_rank: null,
        };
    } catch (error) {
        console.error('[Duels] Erro ao buscar stats:', error.message);
        return null;
    }
};

/**
 * Obtém ranking semanal
 * @param {number} limit - Limite de resultados
 */
export const getWeeklyRanking = async (limit = 100) => {
    try {
        const { data, error } = await supabase
            .rpc('get_weekly_ranking', { p_limit: limit });

        if (error) {
            // Handle missing function gracefully
            if (error.message?.includes('schema cache') ||
                error.message?.includes('does not exist') ||
                error.message?.includes('function')) {
                console.log('[Duels] Função get_weekly_ranking não disponível, retornando vazio');
                return [];
            }
            throw error;
        }
        return data || [];
    } catch (error) {
        // Don't log expected errors
        if (!error.message?.includes('schema cache') && !error.message?.includes('does not exist')) {
            console.error('[Duels] Erro ao buscar ranking:', error.message);
        }
        return [];
    }
};

export default {
    checkChallengeLimit,
    sendChallenge,
    getPendingChallenges,
    getSentChallenges,
    acceptChallenge,
    declineChallenge,
    getActiveDuel,
    getDuelHistory,
    submitDuelAnswer,
    completeDuel,
    joinLiveDuel,
    cancelLiveDuel,
    getUserDuelStats,
    getWeeklyRanking,
};
